import type { PublishConfigTargetAudience } from '@brushfire/types'
import type { AudienceFilters } from '@/features/instruments/publish/target-audience/lib/audienceTypes'

type TargetAudienceStored = PublishConfigTargetAudience & { aiBaseline?: AudienceFilters }

/** Local runtime — `@brushfire/types` named exports can be undefined under Vite ESM/CJS. */
function aiBaselineFromTargetAudience(
  targetAudience: PublishConfigTargetAudience | TargetAudienceStored,
): AudienceFilters | undefined {
  if ('aiBaseline' in targetAudience && targetAudience.aiBaseline != null) {
    return targetAudience.aiBaseline
  }
  return undefined
}

export function readAiSuggestedFilters(
  targetAudience: PublishConfigTargetAudience | TargetAudienceStored | undefined | null,
): AudienceFilters | undefined {
  if (!targetAudience) return undefined
  return targetAudience.aiSuggestedFilters ?? aiBaselineFromTargetAudience(targetAudience)
}

export function activeAudienceFiltersFromTargetAudience(
  targetAudience: PublishConfigTargetAudience | TargetAudienceStored,
): AudienceFilters {
  return targetAudience.filters ?? {}
}

function cloneAudienceFilters(filters: AudienceFilters): AudienceFilters {
  return structuredClone(filters)
}

function isEffectivelyEmptyAudienceFilters(filters: AudienceFilters): boolean {
  if (!filters || typeof filters !== 'object') return true
  const { ageRange, gender, location, education, employment, deviceType } = filters
  if (ageRange?.min != null || ageRange?.max != null) return false
  if (gender?.length) return false
  if (location?.state?.trim() || location?.city?.trim()) return false
  if (education?.length) return false
  if (employment?.length) return false
  if (deviceType?.length) return false
  if (filters.maritalStatus?.length) return false
  if (filters.householdSize?.length) return false
  if (filters.numberOfChildren?.length) return false
  if (filters.industry?.length) return false
  if (filters.housingStatus?.length) return false
  if (filters.senateDistrict?.trim()) return false
  if (filters.ekycStatus && filters.ekycStatus !== 'all') return false
  return true
}

export function canonicalizeTargetAudienceForStorage(
  targetAudience: PublishConfigTargetAudience,
): PublishConfigTargetAudience {
  const aiSuggested = readAiSuggestedFilters(targetAudience) ?? {}
  const modified = targetAudience.filtersModified === true
  const userFilters = cloneAudienceFilters(targetAudience.filters ?? {})

  const aiSuggestedFilters =
    Object.keys(aiSuggested).length > 0
      ? cloneAudienceFilters(aiSuggested)
      : targetAudience.aiSuggestedFilters

  if (isEffectivelyEmptyAudienceFilters(userFilters)) {
    return {
      ...targetAudience,
      filters: {},
      aiSuggestedFilters,
      filtersModified: modified,
      aiSuggestionsApplied: targetAudience.aiSuggestionsApplied === true,
    }
  }

  // BFP-252 — do not copy AI suggestions into active filters.
  return {
    ...targetAudience,
    filters: userFilters,
    aiSuggestedFilters,
    filtersModified: modified,
    aiSuggestionsApplied: targetAudience.aiSuggestionsApplied === true,
  }
}
